import { useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import Input from "../ui/Input";
import LoadingButton from "../ui/LoadingButton";
import { useIntl } from "../../contexts/IntlContext";
import { isValidEmail } from "../../utils/isValidEmail";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  handle: () => void;
  receiptEmail: string;
  setReceiptEmail: (email: string) => void;
  submitting?: boolean;
};

export default function ExportVisitorsDialog({
  open,
  setOpen,
  handle,
  receiptEmail,
  setReceiptEmail,
  submitting = false,
}: Props) {
  const intl = useIntl();
  const [touched, setTouched] = useState(false);

  const emailIsInvalid = touched && receiptEmail.trim().length > 0 && !isValidEmail(receiptEmail);
  const emailIsEmpty = touched && receiptEmail.trim().length === 0;

  const handleClose = () => {
    setTouched(false);
    setOpen(false);
  };

  const handleSubmit = () => {
    setTouched(true);
    if (receiptEmail.trim().length === 0 || !isValidEmail(receiptEmail)) {
      return;
    }
    handle();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="export-visitors-dialog-title"
      aria-describedby="export-visitors-dialog-description"
    >
      <DialogTitle id="export-visitors-dialog-title">
        {intl.formatMessage({ id: "common.confirm" })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="export-visitors-dialog-description">
          {intl.formatMessage({ id: "exportVisitorsDialog.confirmMessage" })}
          <br />
          {intl.formatMessage({ id: "exportVisitorsDialog.explanation" })}
        </DialogContentText>
        <Input
          value={receiptEmail}
          onChange={(e) => setReceiptEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={intl.formatMessage({ id: "exportVisitorsDialog.emailPlaceholder" })}
          error={emailIsInvalid || emailIsEmpty}
          helperText={
            emailIsEmpty
              ? intl.formatMessage({ id: "login.emailRequired" })
              : emailIsInvalid
              ? intl.formatMessage({ id: "validation.invalidEmail" })
              : " "
          }
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} disabled={submitting} color="secondary" fullWidth={false}>
          {intl.formatMessage({ id: "common.cancel" })}
        </LoadingButton>
        <LoadingButton onClick={handleSubmit} loading={submitting} color="primary" fullWidth={false}>
          {intl.formatMessage({ id: "common.export" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
